import { ArrowUpDownIcon, ChevronDownIcon } from 'lucide-react';

import { buttonVariants } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export type WorkspaceSort = 'updated' | 'name';

/**
 * "Recently active", not "Recently updated": the order is the last activity in
 * the workspace, and `updated_at` moves only when the workspace itself is edited.
 */
const SORT_LABEL: Record<WorkspaceSort, string> = {
  updated: 'Recently active',
  name: 'Name',
};

/**
 * The list's order, as one small menu at the end of the list's own row (spec 199
 * US3). It was two buttons beside the search field, which made the row between
 * the recent workspaces and the grid read as a toolbar of three loose widgets.
 */
export function WorkspaceSortMenu({
  sort,
  onSortChange,
  icon = false,
}: {
  sort: WorkspaceSort;
  onSortChange: (sort: WorkspaceSort) => void;
  /**
   * An icon button the search field's height, for the search row on a phone
   * (spec 267). In the tab row it ran past the right gutter and sat 5px above
   * the tabs.
   */
  icon?: boolean;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Sort: ${SORT_LABEL[sort]}`}
        className={cn(
          icon
            ? cn(
                buttonVariants({ variant: 'outline', size: 'icon-lg' }),
                'text-muted-foreground size-10 shrink-0',
              )
            : cn(
                buttonVariants({ variant: 'ghost', size: 'sm' }),
                'text-muted-foreground gap-1 font-normal',
              ),
        )}
      >
        {icon ? (
          <ArrowUpDownIcon aria-hidden="true" strokeWidth={1.5} />
        ) : (
          <>
            {/* The order's name from `sm`: on a phone the row also holds the
                tabs, and "Sort" alone is enough to find it. */}
            {/* One span, so the trigger's gap falls after the colon. */}
            <span>
              Sort<span className="hidden sm:inline">:</span>
            </span>
            <span className="text-foreground hidden sm:inline">
              {SORT_LABEL[sort]}
            </span>
            <ChevronDownIcon aria-hidden="true" className="size-3.5" />
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        <DropdownMenuRadioGroup
          value={sort}
          onValueChange={(next: string) => {
            if (next === 'updated' || next === 'name') onSortChange(next);
          }}
        >
          <DropdownMenuRadioItem value="updated">
            {SORT_LABEL.updated}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="name">
            {SORT_LABEL.name}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
